<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  DEFAULT_PRIMARY_COLOR,
  PRIMARY_COLOR_FAMILIES,
  PRIMARY_COLOR_OPTIONS,
  findPrimaryColor,
  meetsWhiteTextAA,
  readableTextOn,
  whiteTextContrast,
} from 'dashboard/helper/primaryColorHelper';

const props = defineProps({
  modelValue: { type: String, required: true },
});

const emit = defineEmits(['update:modelValue']);
const { t, locale } = useI18n();

const formatRatio = value =>
  new Intl.NumberFormat(locale.value === 'en' ? 'en' : 'pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.floor(value * 10) / 10);

const colorNames = computed(() => ({
  blue: t('APPEARANCE_ACCENT.COLORS.blue'),
  iris: t('APPEARANCE_ACCENT.COLORS.iris'),
  sky: t('APPEARANCE_ACCENT.COLORS.sky'),
  ocean: t('APPEARANCE_ACCENT.COLORS.ocean'),
  cyan: t('APPEARANCE_ACCENT.COLORS.cyan'),
  violet: t('APPEARANCE_ACCENT.COLORS.violet'),
  purple: t('APPEARANCE_ACCENT.COLORS.purple'),
  fuchsia: t('APPEARANCE_ACCENT.COLORS.fuchsia'),
  pink: t('APPEARANCE_ACCENT.COLORS.pink'),
  ruby: t('APPEARANCE_ACCENT.COLORS.ruby'),
  green: t('APPEARANCE_ACCENT.COLORS.green'),
  teal: t('APPEARANCE_ACCENT.COLORS.teal'),
  emerald: t('APPEARANCE_ACCENT.COLORS.emerald'),
  lime: t('APPEARANCE_ACCENT.COLORS.lime'),
  amber: t('APPEARANCE_ACCENT.COLORS.amber'),
  gold: t('APPEARANCE_ACCENT.COLORS.gold'),
  orange: t('APPEARANCE_ACCENT.COLORS.orange'),
  copper: t('APPEARANCE_ACCENT.COLORS.copper'),
  red: t('APPEARANCE_ACCENT.COLORS.red'),
  crimson: t('APPEARANCE_ACCENT.COLORS.crimson'),
  slate: t('APPEARANCE_ACCENT.COLORS.slate'),
  zinc: t('APPEARANCE_ACCENT.COLORS.zinc'),
}));

const familyNames = computed(() => ({
  blues: t('APPEARANCE_ACCENT.FAMILIES.BLUES'),
  purples: t('APPEARANCE_ACCENT.FAMILIES.PURPLES'),
  greens: t('APPEARANCE_ACCENT.FAMILIES.GREENS'),
  warm: t('APPEARANCE_ACCENT.FAMILIES.WARM'),
  neutral: t('APPEARANCE_ACCENT.FAMILIES.NEUTRAL'),
}));

const describeColor = option => {
  const ratio = formatRatio(whiteTextContrast(option.hex));
  const passesAA = meetsWhiteTextAA(option.hex);
  return {
    ...option,
    name: colorNames.value[option.key] || option.key,
    passesAA,
    checkColor: readableTextOn(option.hex),
    contrastText: passesAA
      ? t('APPEARANCE_ACCENT.CONTRAST_AA', { ratio })
      : t('APPEARANCE_ACCENT.CONTRAST_LOW', { ratio }),
  };
};

const colorGroups = computed(() =>
  PRIMARY_COLOR_FAMILIES.map(family => ({
    key: family,
    label: familyNames.value[family],
    colors: PRIMARY_COLOR_OPTIONS.filter(
      option => option.family === family
    ).map(describeColor),
  }))
);

const selectedColor = computed(() =>
  describeColor(findPrimaryColor(props.modelValue))
);
const isDefaultSelected = computed(
  () => selectedColor.value.key === DEFAULT_PRIMARY_COLOR
);

const swatchLabel = color => {
  const label = t('APPEARANCE_ACCENT.SWATCH_LABEL', {
    name: color.name,
    contrast: color.contrastText,
  });
  return color.key === selectedColor.value.key
    ? `${label} (${t('APPEARANCE_ACCENT.SELECTED')})`
    : label;
};

const selectColor = key => emit('update:modelValue', key);
const restoreDefault = () => selectColor(DEFAULT_PRIMARY_COLOR);
</script>

<template>
  <section
    class="rounded-2xl border border-n-weak bg-n-solid-2 p-5"
    data-testid="accent-color-picker"
  >
    <div
      class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="grid size-10 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-hugeicons-paint-board" class="size-5" />
        </span>
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('APPEARANCE_ACCENT.TITLE') }}
          </h3>
          <p class="mb-0 text-xs text-n-slate-10">
            {{ t('APPEARANCE_ACCENT.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-xs font-medium text-n-slate-12 transition hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="isDefaultSelected"
        :title="t('APPEARANCE_ACCENT.DEFAULT_HINT')"
        data-testid="accent-color-restore"
        @click="restoreDefault"
      >
        <Icon icon="i-hugeicons-arrow-turn-backward" class="size-3.5" />
        {{ t('APPEARANCE_ACCENT.RESTORE') }}
      </button>
    </div>

    <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_17rem]">
      <div
        class="flex min-w-0 flex-col gap-4"
        role="group"
        :aria-label="t('APPEARANCE_ACCENT.GROUP_LABEL')"
      >
        <div
          v-for="group in colorGroups"
          :key="group.key"
          class="grid gap-2 sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:items-center"
          :data-testid="`accent-family-${group.key}`"
        >
          <p
            class="mb-0 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ group.label }}
          </p>
          <div class="flex flex-wrap gap-2.5">
            <button
              v-for="color in group.colors"
              :key="color.key"
              type="button"
              class="group relative grid size-9 place-items-center rounded-full transition-transform duration-150 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-n-slate-12 motion-reduce:transition-none motion-reduce:hover:scale-100"
              :class="
                color.key === selectedColor.key &&
                'ring-2 ring-n-slate-12 ring-offset-2 ring-offset-n-solid-2'
              "
              :style="{ backgroundColor: color.hex }"
              :aria-label="swatchLabel(color)"
              :aria-pressed="color.key === selectedColor.key"
              :data-color="color.key"
              @click="selectColor(color.key)"
            >
              <Icon
                v-if="color.key === selectedColor.key"
                icon="i-hugeicons-tick-02"
                class="size-4"
                :style="{ color: color.checkColor }"
              />
              <span
                class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-n-slate-12 px-2 py-1 text-[11px] font-medium text-n-slate-1 opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                aria-hidden="true"
              >
                {{ color.name }}
              </span>
            </button>
          </div>
        </div>
        <p
          class="mb-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-n-slate-11"
          aria-live="polite"
          data-testid="accent-color-current"
        >
          <span class="font-semibold text-n-slate-12">
            {{ t('APPEARANCE_ACCENT.CURRENT', { name: selectedColor.name }) }}
          </span>
          <span
            class="inline-flex items-center gap-1"
            :class="
              selectedColor.passesAA ? 'text-n-teal-11' : 'text-n-amber-11'
            "
          >
            <Icon
              :icon="
                selectedColor.passesAA
                  ? 'i-hugeicons-checkmark-circle-02'
                  : 'i-hugeicons-alert-circle'
              "
              class="size-3.5"
            />
            {{ selectedColor.contrastText }}
          </span>
        </p>
      </div>

      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        data-testid="accent-color-preview"
      >
        <p class="mb-0 text-xs font-semibold text-n-slate-12">
          {{ t('APPEARANCE_ACCENT.PREVIEW.TITLE') }}
        </p>
        <p class="mb-3 mt-0.5 text-[11px] text-n-slate-10">
          {{ t('APPEARANCE_ACCENT.PREVIEW.DESCRIPTION') }}
        </p>
        <div class="grid gap-1" aria-hidden="true">
          <span
            class="flex items-center gap-2 rounded-lg bg-n-brand/10 px-2.5 py-1.5 text-xs font-semibold text-n-brand"
          >
            <Icon icon="i-hugeicons-message-multiple-01" class="size-4" />
            {{ t('APPEARANCE_ACCENT.PREVIEW.MENU_ACTIVE') }}
            <span
              class="ml-auto rounded-full bg-n-brand px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
            >
              {{ t('APPEARANCE_ACCENT.PREVIEW.BADGE') }}
            </span>
          </span>
          <span
            class="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-n-slate-11"
          >
            <Icon icon="i-hugeicons-contact-01" class="size-4" />
            {{ t('APPEARANCE_ACCENT.PREVIEW.MENU_ITEM') }}
          </span>
        </div>
        <div
          class="mt-3 flex flex-wrap items-center gap-3 border-t border-n-weak pt-3"
          aria-hidden="true"
        >
          <span
            class="inline-flex h-8 items-center rounded-lg bg-n-brand px-3 text-xs font-semibold text-white"
          >
            {{ t('APPEARANCE_ACCENT.PREVIEW.BUTTON') }}
          </span>
          <span class="text-xs font-medium text-n-brand underline">
            {{ t('APPEARANCE_ACCENT.PREVIEW.LINK') }}
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
